/**
 * CSV Parser Utility
 * 
 * Handles CSV and Excel file parsing for bulk initiative upload.
 * Provides validation, preview, and duplicate detection.
 * 
 * @module utils/csvParser
 */

import ValidationService from '../services/ValidationService.js';
import InitiativeService from '../services/InitiativeService.js';

export class CSVParser {
  constructor() {
    // Column mappings for flexibility
    this.columnMappings = {
      // Primary columns
      'title': ['title', 'name', 'initiative', 'objective title'],
      'description': ['description', 'details', 'summary', 'overview'],
      'objective': ['objective', 'goal', 'outcome', 'business objective'],
      'owner': ['owner', 'responsible', 'lead', 'assigned to', 'reporter'],
      'priority': ['priority', 'importance', 'level'],
      'status': ['status', 'state', 'progress status'],
      'progress': ['progress', 'completion', '% complete', 'percent complete'],
      
      // Additional columns
      'keyResults': ['key results', 'kr', 'metrics', 'success metrics'],
      'strategicAlignment': ['strategic alignment', 'strategy pillar', 'pillar', 'strategic pillar'],
      'startDate': ['start date', 'start', 'begin date', 'from'],
      'endDate': ['end date', 'end', 'due date', 'deadline', 'to'],
      'type': ['type', 'category', 'initiative type'],
      
      // Legacy columns
      'stakeholders': ['stakeholders', 'participants', 'team'],
      'budget': ['budget', 'cost', 'investment'],
      'risks': ['risks', 'challenges', 'issues'],
      // New: dependencies and risk level/note
      'dependsOn': ['depends on', 'dependencies', 'blocks', 'blocked by'],
      'riskLevel': ['risk level', 'risk', 'risk score'],
      'riskNote': ['risk note', 'risk description', 'risk details']
    };
    
    // Valid values for enum fields
    this.validValues = {
      priority: ['low', 'medium', 'high', 'critical'],
      status: ['not-started', 'in-progress', 'at-risk', 'completed', 'cancelled'],
      type: ['Goal', 'Challenge', 'Demand', 'Initiative']
    };
  }
  
  /**
   * Parse CSV text into structured data
   * @param {string} csvText - Raw CSV text
   * @returns {Object} Parsed result with data and errors
   */
  parseCSV(csvText) {
    try {
      // Split into lines
      const lines = csvText.split(/\r?\n/).filter(line => line.trim());
      
      if (lines.length < 2) {
        return {
          success: false,
          error: 'CSV must have at least a header row and one data row',
          data: []
        };
      }
      
      // Parse header
      const headers = this.parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
      
      // Map headers to our schema
      const columnMap = this.mapColumns(headers);
      
      // Check for required columns
      const requiredColumns = ['title', 'objective', 'owner'];
      const missingColumns = requiredColumns.filter(col => !columnMap[col]);
      
      if (missingColumns.length > 0) {
        return {
          success: false,
          error: `Missing required columns: ${missingColumns.join(', ')}`,
          headers,
          data: []
        };
      }
      
      // Parse data rows
      const data = [];
      const errors = [];
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        const values = this.parseCSVLine(line);
        const row = this.mapRowToInitiative(values, columnMap, headers);
        
        // Validate row
        const validation = this.validateRow(row, i + 1);
        if (validation.valid) {
          data.push(row);
        } else {
          errors.push({
            row: i + 1,
            errors: validation.errors,
            data: row
          });
        }
      }
      
      return {
        success: true,
        data,
        errors,
        headers,
        totalRows: lines.length - 1,
        validRows: data.length,
        invalidRows: errors.length
      };
      
    } catch (error) {
      return {
        success: false,
        error: `Parse error: ${error.message}`,
        data: []
      };
    }
  }
  
  /**
   * Parse a single CSV line handling quotes and commas
   * @param {string} line - CSV line
   * @returns {Array} Parsed values
   */
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // Toggle quote mode
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        // End of field
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    // Add last field
    values.push(current.trim());
    
    return values;
  }
  
  /**
   * Map CSV headers to our schema columns
   * @param {Array} headers - CSV headers
   * @returns {Object} Column mapping
   */
  mapColumns(headers) {
    const mapping = {};
    
    headers.forEach((header, index) => {
      const cleanHeader = header.toLowerCase().trim();
      
      // Check each possible column
      for (const [field, variations] of Object.entries(this.columnMappings)) {
        if (variations.some(v => v === cleanHeader || cleanHeader.includes(v))) {
          mapping[field] = index;
          break;
        }
      }
    });
    
    return mapping;
  }
  
  /**
   * Map a CSV row to an initiative object
   * @param {Array} values - Row values
   * @param {Object} columnMap - Column mapping
   * @param {Array} headers - Original headers
   * @returns {Object} Initiative object
   */
  mapRowToInitiative(values, columnMap, headers) {
    const initiative = {
      title: '',
      description: '',
      objective: '',
      owner: '',
      priority: 'medium',
      status: 'not-started',
      progress: 0,
      keyResults: [],
      type: 'Initiative'
    };
    
    // Map basic fields
    for (const [field, index] of Object.entries(columnMap)) {
      if (index !== undefined && values[index]) {
        const value = values[index].trim();
        
        // Handle special fields
        if (field === 'keyResults') {
          // Split by semicolon or pipe
          initiative.keyResults = value.split(/[;|]/).map(kr => kr.trim()).filter(kr => kr);
        } else if (field === 'progress') {
          // Parse percentage
          const num = parseFloat(value.replace('%', ''));
          initiative.progress = isNaN(num) ? 0 : Math.min(100, Math.max(0, num));
        } else if (field === 'priority') {
          // Normalize priority
          const normalized = value.toLowerCase();
          initiative.priority = this.validValues.priority.includes(normalized) ? normalized : 'medium';
        } else if (field === 'status') {
          // Normalize status
          const normalized = value.toLowerCase().replace(' ', '-');
          initiative.status = this.validValues.status.includes(normalized) ? normalized : 'not-started';
        } else if (field === 'type') {
          // Normalize type
          const normalized = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
          initiative.type = this.validValues.type.includes(normalized) ? normalized : 'Initiative';
        } else if (field === 'startDate' || field === 'endDate') {
          // Parse date
          const date = this.parseDate(value);
          if (date) {
            initiative[field] = date;
          }
        } else {
          // Direct mapping
          initiative[field] = value;
        }
      }
    }
    
    // Add any unmapped columns as metadata
    const metadata = {};
    headers.forEach((header, index) => {
      const isMapped = Object.values(columnMap).includes(index);
      if (!isMapped && values[index]) {
        metadata[header] = values[index];
      }
    });
    
    if (Object.keys(metadata).length > 0) {
      initiative.metadata = metadata;
    }
    
    return initiative;
  }
  
  /**
   * Parse various date formats
   * @param {string} dateStr - Date string
   * @returns {string|null} ISO date string or null
   */
  parseDate(dateStr) {
    if (!dateStr) return null;
    
    // Try different date formats
    const formats = [
      // ISO format
      /^\d{4}-\d{2}-\d{2}$/,
      // US format MM/DD/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,
      // EU format DD/MM/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,
      // Dot format DD.MM.YYYY
      /^\d{1,2}\.\d{1,2}\.\d{4}$/
    ];
    
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      // Continue trying
    }
    
    return null;
  }
  
  /**
   * Validate a single row
   * @param {Object} row - Initiative data
   * @param {number} rowNumber - Row number for error reporting
   * @returns {Object} Validation result
   */
  validateRow(row, rowNumber) {
    const errors = [];
    
    // Required fields
    if (!row.title || row.title.length < 3) {
      errors.push('Title is required (min 3 characters)');
    }
    
    if (!row.objective || row.objective.length < 10) {
      errors.push('Objective is required (min 10 characters)');
    }
    
    if (!row.owner || row.owner.length < 2) {
      errors.push('Owner is required');
    }
    
    // Field lengths
    if (row.title && row.title.length > 200) {
      errors.push('Title too long (max 200 characters)');
    }
    
    if (row.description && row.description.length > 5000) {
      errors.push('Description too long (max 5000 characters)');
    }
    
    // Date validation
    if (row.startDate && row.endDate) {
      const start = new Date(row.startDate);
      const end = new Date(row.endDate);
      if (start > end) {
        errors.push('Start date cannot be after end date');
      }
    }
    
    return {
      valid: errors.length === 0,
      errors
    };
  }
  
  /**
   * Check for duplicates against existing initiatives
   * @param {Array} initiatives - Initiatives to check
   * @returns {Object} Duplicate analysis
   */
  async checkDuplicates(initiatives) {
    const existingInitiatives = InitiativeService.getInitiatives();
    const duplicates = [];
    const similar = [];
    
    initiatives.forEach((newInit, index) => {
      existingInitiatives.forEach(existing => {
        // Exact title match
        if (newInit.title.toLowerCase() === existing.title.toLowerCase()) {
          duplicates.push({
            index,
            newInitiative: newInit,
            existingInitiative: existing,
            matchType: 'exact_title'
          });
        }
        // Similar objective
        else if (this.calculateSimilarity(newInit.objective, existing.objective) > 0.8) {
          similar.push({
            index,
            newInitiative: newInit,
            existingInitiative: existing,
            similarity: this.calculateSimilarity(newInit.objective, existing.objective),
            matchType: 'similar_objective'
          });
        }
      });
    });
    
    return {
      hasDuplicates: duplicates.length > 0,
      hasSimilar: similar.length > 0,
      duplicates,
      similar,
      summary: {
        total: initiatives.length,
        duplicateCount: duplicates.length,
        similarCount: similar.length,
        uniqueCount: initiatives.length - duplicates.length
      }
    };
  }
  
  /**
   * Calculate text similarity (simple algorithm)
   * @param {string} text1 - First text
   * @param {string} text2 - Second text
   * @returns {number} Similarity score (0-1)
   */
  calculateSimilarity(text1, text2) {
    const words1 = new Set(text1.toLowerCase().split(/\s+/));
    const words2 = new Set(text2.toLowerCase().split(/\s+/));
    
    const intersection = new Set([...words1].filter(x => words2.has(x)));
    const union = new Set([...words1, ...words2]);
    
    return union.size > 0 ? intersection.size / union.size : 0;
  }
  
  /**
   * Generate sample CSV template
   * @returns {string} CSV template
   */
  static generateTemplate() {
    return `Title,Description,Objective,Owner,Priority,Status,Key Results,Strategic Alignment,Start Date,End Date
"Improve Customer Retention","Implement new customer success program","Reduce customer churn by 15% through proactive engagement","Jane Smith","high","in-progress","Churn rate < 5%;NPS > 70;Support tickets reduced 30%","Customer Excellence","2025-01-01","2025-06-30"
"Modernize IT Infrastructure","Upgrade legacy systems to cloud","Increase system reliability to 99.9% uptime","John Doe","critical","not-started","Zero critical outages;Response time < 2sec;Cost reduction 20%","Operational Excellence","2025-02-01","2025-12-31"
"Launch Mobile App","Develop and release mobile application","Increase customer engagement by 40% via mobile channel","Sarah Johnson","medium","in-progress","10k downloads in Q1;4.5 star rating;30% DAU","Digital Innovation","2025-01-15","2025-04-30"`;
  }
  
  /**
   * Export initiatives to CSV
   * @param {Array} initiatives - Initiatives to export
   * @returns {string} CSV string
   */
  static exportToCSV(initiatives) {
    // Define columns to export
    const columns = [
      'title', 'description', 'objective', 'owner', 
      'priority', 'status', 'progress', 'type',
      'keyResults', 'strategicAlignment', 
      'startDate', 'endDate'
    ];
    
    // Create header row
    const headers = columns.map(col => {
      // Convert camelCase to Title Case
      return col.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    });
    
    // Create data rows
    const rows = initiatives.map(init => {
      return columns.map(col => {
        const value = init[col];
        
        // Handle special cases
        if (col === 'keyResults' && Array.isArray(value)) {
          return `"${value.join(';')}"`;
        } else if (value === undefined || value === null) {
          return '';
        } else if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
          // Escape quotes and wrap in quotes
          return `"${value.replace(/"/g, '""')}"`;
        } else {
          return value;
        }
      });
    });
    
    // Combine into CSV
    const csvLines = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ];
    
    return csvLines.join('\n');
  }
}

// Create singleton instance
export const csvParser = new CSVParser();

// Attach to window for debugging
if (import.meta.env.DEV) {
  window.csvParser = csvParser;
  window.CSVParser = CSVParser;
}
